import React, {useContext} from 'react';
import * as Theme from '@sail/theme-classic';
import type {ReactNode, RefObject} from 'react';
import {useId} from '@sail/react-aria';
import {warning} from '@sail/icons/react/Icon';
import {FormFieldMetadataContext as FormFieldContext} from '../../components/FormField';

import {Icon} from './Icon';

import {view} from '../view';
import {css} from '../css';

import {
  useInputId,
  LABEL_OPT_OUT_STRING,
} from '../../components/hooks/useInputId';
import {Label} from './Label';
import {CompositeFieldContext} from './CompositeFieldContext';

export type ControlProps = {
  /**
   * Visually hides the specified elements. The hidden elements will still be present and visible to screen readers.
   * @external
   */
  hiddenElements?: FormFieldElements[];
  /**
   * Descriptive text that will be rendered adjacent to the control's label.
   * @external
   */
  description?: ReactNode;
  /**
   * Error text that will be rendered below the control.
   * @external
   */
  error?: ReactNode;
  /**
   * ref that attaches to the outermost element of the control.
   */
  outerRef?: RefObject<HTMLDivElement>;
  /**
   * Text that describes the control. Will be both visible and clickable.
   * @external
   */
  label?: ReactNode;
};

export type OneOfLabelOrAriaLabel =
  | {
      /**
       * Text that describes the control. Will be both visible and clickable.
       * @external
       */
      label?: ReactNode;
      /**
       * Text that describes the control. Only visible to screen readers, and is not clickable. Should not be used if `label` is set.
       * @external
       */
      'aria-label': string;
    }
  | {
      /**
       * Text that describes the control. Will be both visible and clickable.
       * @external
       */
      label: ReactNode;
      /**
       * Text that describes the control. Only visible to screen readers, and is not clickable. Should not be used if `label` is set.
       * @external
       */
      'aria-label'?: string;
    };

export type Layouts = 'inline' | 'default';

export type FieldMetadata<T extends string> = Record<
  T,
  {id?: string; present?: boolean}
>;

export type SubsetOfKeys<T, U extends keyof T> = U;

export type FormFieldElements = SubsetOfKeys<
  ControlProps,
  'error' | 'description' | 'label'
>;

export type FormFieldMetadata = FieldMetadata<FormFieldElements>;

export const ErrorText = view.div.create<{id: string}>(
  ({children, ...props}, View) => (
    <View
      uses={[props]}
      css={{
        stack: 'x',
        gap: 'xsmall',
        color: 'critical',
        font: 'caption',
      }}
    >
      <view.div>
        <Icon
          icon={warning}
          css={{
            fill: 'critical',
            transform: css.value('translateY(2px)'),
          }}
          size="xsmall"
        />
      </view.div>
      <view.div
        css={{
          width: 'fill',
          alignSelfX: 'start',
        }}
      >
        {children}
      </view.div>
    </View>
  ),
);

export const Description = view.div.create<{id: string}>(
  ({children, ...props}, View) => (
    <View
      uses={[props]}
      css={{
        color: 'secondary',
        font: 'caption',
      }}
    >
      {children}
    </View>
  ),
);

export function formFieldGenerator<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Element: any,
  options: {layout: Layouts} = {layout: 'default'},
) {
  const FormField = view.div.create<
    ControlProps & {
      layout?: Layouts;
    }
  >(
    (
      {
        label,
        description,
        error,
        hiddenElements = [],
        outerRef,
        layout = 'default',
        id: propsId,
        children,
        ...props
      },
      Div,
    ) => {
      const {id, rootNode} = useInputId({
        label,
        outerRef,
        id: propsId,
      });
      const descriptionId = useId();
      const errorId = useId();
      const labelId = useId();
      const shouldRenderLabel = Boolean(
        label && label !== LABEL_OPT_OUT_STRING,
      );

      const contextValue = useContext(CompositeFieldContext);
      const isInCompositeField = contextValue?.isInCompositeField;

      const shouldHideLabel =
        isInCompositeField || hiddenElements.includes('label');
      const shouldHideDescription =
        isInCompositeField || hiddenElements.includes('description');
      const shouldHideError =
        isInCompositeField || hiddenElements.includes('error');
      const bothLabelAndDescriptionHidden =
        (!shouldRenderLabel && !description) ||
        (shouldHideLabel && (!description || shouldHideDescription)) ||
        (shouldHideDescription && (!shouldRenderLabel || shouldHideLabel)) ||
        (shouldHideLabel && shouldHideDescription);

      const layouts: Record<Layouts, () => JSX.Element> = {
        inline: () => (
          <view.div css={{stack: 'x', gap: 'small'}} ref={rootNode}>
            <view.div
              css={{
                marginTop: 'xxsmall',
                lineHeight: css.value('1px'),
                flexGrow: css.value(0),
              }}
            >
              {children}
            </view.div>
            <view.div css={{stack: 'y', gap: 'xsmall'}}>
              {shouldRenderLabel ? (
                <Label
                  uses={[shouldHideLabel ? Theme.visuallyHidden : null]}
                  htmlFor={id}
                  id={labelId}
                  css={{width: 'fit'}}
                >
                  {label}
                </Label>
              ) : null}
              {description ? (
                <Description
                  uses={[shouldHideDescription ? Theme.visuallyHidden : null]}
                  id={descriptionId}
                >
                  {description}
                </Description>
              ) : null}
              {error ? (
                <ErrorText
                  uses={[shouldHideError ? Theme.visuallyHidden : null]}
                  id={errorId}
                >
                  {error}
                </ErrorText>
              ) : null}
            </view.div>
          </view.div>
        ),
        default: () => (
          <view.div css={{stack: 'y', gap: 'xsmall'}} ref={rootNode}>
            <view.div
              uses={[
                bothLabelAndDescriptionHidden ? Theme.visuallyHidden : null,
              ]}
              css={{stack: 'y', gap: css.value(0)}}
            >
              {shouldRenderLabel ? (
                <Label
                  uses={[shouldHideLabel ? Theme.visuallyHidden : null]}
                  htmlFor={id}
                  id={labelId}
                >
                  {label}
                </Label>
              ) : null}
              {description ? (
                <Description
                  uses={[shouldHideDescription ? Theme.visuallyHidden : null]}
                  id={descriptionId}
                >
                  {description}
                </Description>
              ) : null}
            </view.div>
            <view.div css={{stack: 'y'}}>{children}</view.div>
            {error ? (
              <ErrorText
                uses={[shouldHideError ? Theme.visuallyHidden : null]}
                id={errorId}
              >
                {error}
              </ErrorText>
            ) : null}
          </view.div>
        ),
      };

      return (
        <Div
          css={{
            layout: 'column',
          }}
          uses={[props]}
        >
          <FormFieldContext.Provider
            value={{
              elements: {
                description: {
                  id: descriptionId,
                  present: Boolean(description),
                },
                label: {
                  id: labelId,
                  present: shouldRenderLabel,
                },
                error: {
                  id: errorId,
                  present: Boolean(error),
                },
              },
              controlId: id,
            }}
          >
            {layouts[layout]()}
          </FormFieldContext.Provider>
        </Div>
      );
    },
  );
  // we probably want a way to give component developers more control over this in the future
  const controlProps = [
    'autoComplete',
    'autoFocus',
    'checked',
    'children',
    'cols',
    'defaultChecked',
    'defaultValue',
    'disabled',
    'form',
    'invalid',
    'indeterminate',
    'inputMode',
    'maxLength',
    'minLength',
    'multiple',
    'name',
    'onChange',
    'onKeyPress',
    'onKeyDown',
    'onKeyUp',
    'pattern',
    'placeholder',
    'readOnly',
    'ref',
    'required',
    'resizeable',
    'rows',
    'size',
    'spellcheck',
    'step',
    'tabIndex',
    'type',
    'value',
    'wrap',
  ];

  return FormField.extend<T & ControlProps & OneOfLabelOrAriaLabel>(
    (props, View) => {
      const propsForControl: Record<string, unknown> = {};
      const propsForFormField: Record<string, unknown> = {};

      Object.entries(props).forEach(([key, value]) => {
        if (controlProps.includes(key) || key.startsWith('aria-')) {
          propsForControl[key] = value;
        } else {
          propsForFormField[key] = value;
        }
      });

      return (
        <View {...propsForFormField} layout={options.layout}>
          <Element {...propsForControl} />
        </View>
      );
    },
  );
}
